import { useEffect, useMemo, useRef, useState } from 'react';
import NoticeService from '@/shared/services/NoticeService';
import { parseNoticeSearchByDocumentResponse } from '@/shared/utils/noticeSlug';

const CONSULTATION_DOCUMENT_NOTIFICATION_TYPES = new Set([
  'CONSULTATION_COMMENT_CREATED',
  'DOCUMENT_COMMENT_STATUS_CHANGED',
  'DOCUMENT_COMMENT_SUBMITTED_FOR_REVIEW',
  'MODERATOR_ACTION_REQUIRED',
]);

function notificationType(n) {
  return n?.notification_type || n?.type || '';
}

function collectConsultationDocumentIds(notifications) {
  return [
    ...new Set(
      (notifications || [])
        .filter((n) => CONSULTATION_DOCUMENT_NOTIFICATION_TYPES.has(notificationType(n)))
        .map((n) => n?.related_entity_id ?? n?.relatedEntityId)
        .filter((id) => id != null && id !== '')
        .map(String)
    ),
  ];
}

/**
 * Učitava podatke objave po document_id (related_entity_id) za konsultacijska obaveštenja.
 *
 * @param {{ enabled?: boolean, notifications?: object[] }} [options]
 * @param {boolean} [options.enabled] - Da li je hook aktivan.
 * @param {object[]} [options.notifications] - Lista obaveštenja sa document related_entity_id.
 * @returns {{ consultationByDocumentId: Record<string, { documentId: string, noticeId: string, title: string, slug: string }> }}
 */
export function useNotificationConsultationNotices({
  enabled = false,
  notifications = [],
} = {}) {
  const [consultationByDocumentId, setConsultationByDocumentId] = useState({});
  const fetchStateRef = useRef({});

  const documentIds = useMemo(
    () => collectConsultationDocumentIds(notifications),
    [notifications]
  );

  const shouldFetch = enabled && documentIds.length > 0;

  useEffect(() => {
    if (!shouldFetch) return undefined;

    const missingIds = documentIds.filter((id) => {
      const state = fetchStateRef.current[id];
      return state !== 'pending' && state !== 'done';
    });

    if (!missingIds.length) return undefined;

    let cancelled = false;
    missingIds.forEach((id) => {
      fetchStateRef.current[id] = 'pending';
    });

    (async () => {
      const updates = {};

      await Promise.all(
        missingIds.map(async (documentId) => {
          try {
            const raw = await NoticeService.searchByDocument(documentId);
            const parsed = parseNoticeSearchByDocumentResponse(raw);
            if (parsed) {
              updates[documentId] = {
                documentId,
                noticeId: parsed.noticeId,
                title: parsed.title,
                slug: parsed.slug,
              };
            }
          } catch {
            /* notice may be unavailable */
          } finally {
            fetchStateRef.current[documentId] = 'done';
          }
        })
      );

      if (!cancelled && Object.keys(updates).length > 0) {
        setConsultationByDocumentId((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, documentIds]);

  return {
    consultationByDocumentId,
  };
}
